import type React from 'react';
import { useMemo } from 'react';
import PieChart from '@/components/UI/Charts/PieChart/index.tsx';
import { getChartColors } from '@/utils/colorHelpers.ts';
import { createCustomTooltipPlugin } from './customTooltipPlugin';
import type { useAssistanceTypeChartData } from './hooks/useAssistanceTypeChartData';
import SectionCard, { type SectionCardProps } from './SectionCard';

type AssistanceTypeEntry = ReturnType<typeof useAssistanceTypeChartData>['entries'][number];

interface AssistanceTypeChartProps extends Omit<SectionCardProps, 'title' | 'children'> {
  entries: AssistanceTypeEntry[];
}

const AssistanceTypeChart: React.FC<AssistanceTypeChartProps> = ({ entries, className }) => {
  const colors = getChartColors(entries.length);

  const tooltipPlugin = useMemo(
    () =>
      createCustomTooltipPlugin({
        customData: entries.map((e) => `${e.formatted} (${e.percent}%)`),
      }),
    [entries]
  );
  return (
    <SectionCard title='Type of Assistance Covered By Payment' className={`min-h-[260px]  ${className ?? ''}`}>
      <div className='flex flex-row w-full h-full min-h-[200px] relative overflow-visible'>
        {/* Left: Labels/Legend */}
        <div className='flex-1 flex flex-col justify-end min-h-[180px]'>
          <div className='space-y-2 mb-2'>
            {entries.map((entry, i) => (
              <div key={entry.label} className='flex items-center text-neutralColor-normal text-xs'>
                <span className='inline-block w-3 h-3 rounded-full mr-2 shrink-0' style={{ background: colors[i] }} />
                <span className='font-medium'>{entry.label}</span>
                <span className='ml-1 font-bold text-sm'>: {entry.formatted}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Right: Chart */}
        <div className='shrink-0 w-[180px] h-[180px] flex items-center justify-center relative overflow-visible'>
          <PieChart
            labels={entries.map((e) => e.label)}
            datasets={entries.map((e, i) => ({
              data: e.value,
              color: colors[i],
              formattedData: `${e.formatted} (${e.percent}%)`,
            }))}
            showValue={false}
            showValueAndPercentage={true}
            currency={false}
            displayLegend={false}
            maintainAspectRatio={false}
            tooltipPlugin={tooltipPlugin}
          />
        </div>
      </div>
    </SectionCard>
  );
};

export default AssistanceTypeChart;
